import { OAuthError } from '@auth0/auth0-react';
import SentryService from '@services/SentryService';

/**
 * Auth0 OAuth2 error codes that we handle explicitly
 */
export const AUTH0_ERROR_CODES = {
  INVALID_GRANT: 'invalid_grant',
  LOGIN_REQUIRED: 'login_required',
  CONSENT_REQUIRED: 'consent_required',
  MISSING_REFRESH_TOKEN: 'missing_refresh_token',
  TIMEOUT: 'timeout',
  POPUP_CLOSED: 'popup_closed',
  POPUP_BLOCKED: 'popup_blocked',
  UNKNOWN: 'unknown',
} as const;

export type Auth0ErrorCode = (typeof AUTH0_ERROR_CODES)[keyof typeof AUTH0_ERROR_CODES];

/**
 * Structured response for auth error handling
 */
export interface AuthErrorResponse {
  shouldRetryWithPopup: boolean;
  shouldClearToken: boolean;
  shouldRequireLogin: boolean;
  errorCode: Auth0ErrorCode;
  originalError: unknown;
}

/**
 * Type guard to check if an error is an OAuth error with an error code
 */
export function isOAuthError(error: unknown): error is OAuthError {
  return error instanceof OAuthError;
}

/**
 * Type guard for errors with an error property (Auth0 API errors)
 */
export function isAuth0ApiError(
  error: unknown,
): error is { error: string; error_description?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error: unknown }).error === 'string'
  );
}

/**
 * Centralized Auth0 error handler
 * Analyzes errors and returns a structured response for consistent handling
 * Using object instead of class to avoid ESLint no-extraneous-class
 */
export const Auth0ErrorHandler = {
  /**
   * Analyze an auth error and determine the appropriate response
   */
  analyze(error: unknown): AuthErrorResponse {
    // Handle OAuth errors (from Auth0 SDK)
    if (isOAuthError(error)) {
      return this.handleOAuthError(error);
    }

    // Handle API errors (from Auth0 API responses)
    if (isAuth0ApiError(error)) {
      return this.handleApiError(error);
    }

    // Handle unknown errors
    return this.handleUnknownError(error);
  },

  handleOAuthError(error: OAuthError): AuthErrorResponse {
    // Log to Sentry with appropriate context
    this.logError(error, error.error);

    switch (error.error) {
      case AUTH0_ERROR_CODES.INVALID_GRANT:
        // Invalid refresh token - need to clear and re-authenticate
        return {
          shouldRetryWithPopup: false,
          shouldClearToken: true,
          shouldRequireLogin: true,
          errorCode: AUTH0_ERROR_CODES.INVALID_GRANT,
          originalError: error,
        };

      case AUTH0_ERROR_CODES.LOGIN_REQUIRED:
      case AUTH0_ERROR_CODES.MISSING_REFRESH_TOKEN:
        // No valid session - try popup first, then require login
        return {
          shouldRetryWithPopup: true,
          shouldClearToken: false,
          shouldRequireLogin: true, // Only if popup fails
          errorCode: error.error as Auth0ErrorCode,
          originalError: error,
        };

      case AUTH0_ERROR_CODES.CONSENT_REQUIRED:
        // User needs to consent - popup is the right approach
        return {
          shouldRetryWithPopup: true,
          shouldClearToken: false,
          shouldRequireLogin: false,
          errorCode: AUTH0_ERROR_CODES.CONSENT_REQUIRED,
          originalError: error,
        };

      case AUTH0_ERROR_CODES.TIMEOUT:
        // Network timeout - might be temporary, try popup
        return {
          shouldRetryWithPopup: true,
          shouldClearToken: false,
          shouldRequireLogin: true, // Only if popup fails
          errorCode: AUTH0_ERROR_CODES.TIMEOUT,
          originalError: error,
        };

      case AUTH0_ERROR_CODES.POPUP_CLOSED:
        // User closed popup - don't retry
        return {
          shouldRetryWithPopup: false,
          shouldClearToken: false,
          shouldRequireLogin: true,
          errorCode: AUTH0_ERROR_CODES.POPUP_CLOSED,
          originalError: error,
        };

      case AUTH0_ERROR_CODES.POPUP_BLOCKED:
        // Popup was blocked - don't retry with popup, go straight to login
        return {
          shouldRetryWithPopup: false,
          shouldClearToken: false,
          shouldRequireLogin: true,
          errorCode: AUTH0_ERROR_CODES.POPUP_BLOCKED,
          originalError: error,
        };

      default:
        // Unknown OAuth error - be conservative
        return {
          shouldRetryWithPopup: false,
          shouldClearToken: false,
          shouldRequireLogin: true,
          errorCode: AUTH0_ERROR_CODES.UNKNOWN,
          originalError: error,
        };
    }
  },

  handleApiError(error: { error: string; error_description?: string }): AuthErrorResponse {
    // API errors have the same structure as OAuth errors
    const oauthError = new OAuthError(error.error, error.error_description);
    return this.handleOAuthError(oauthError);
  },

  handleUnknownError(error: unknown): AuthErrorResponse {
    // Check if this is a popup blocked error (often comes as a generic Error)
    if (error instanceof Error) {
      // Common popup blocked error messages
      if (
        error.message.includes('popup_blocked') ||
        error.message.includes('Popup blocked') ||
        error.message.includes('Pop-up blocked') ||
        error.message.includes('window.open') ||
        error.message.includes('Failed to open')
      ) {
        SentryService.logEvent('Popup blocked by browser', {
          level: 'warning',
          tags: { type: 'auth_error', error_code: 'popup_blocked' },
          extra: { errorMessage: error.message },
        });

        return {
          shouldRetryWithPopup: false,
          shouldClearToken: false,
          shouldRequireLogin: true,
          errorCode: AUTH0_ERROR_CODES.POPUP_BLOCKED,
          originalError: error,
        };
      }
    }

    // Log unknown errors
    SentryService.captureException(error, {
      level: 'error',
      tags: { type: 'auth_unknown_error' },
      extra: { error },
    });

    // For unknown errors, be conservative and require re-authentication
    return {
      shouldRetryWithPopup: false,
      shouldClearToken: true,
      shouldRequireLogin: true,
      errorCode: AUTH0_ERROR_CODES.UNKNOWN,
      originalError: error,
    };
  },

  logError(error: OAuthError, errorCode: string): void {
    const level = this.getErrorLevel(errorCode);

    SentryService.logEvent(`Auth0 ${errorCode}: ${error.error_description || 'No description'}`, {
      level,
      tags: {
        type: 'auth_error',
        error_code: errorCode,
        error_type: 'oauth',
      },
      extra: {
        error: error.error,
        description: error.error_description,
        stack: error.stack,
      },
    });
  },

  getErrorLevel(errorCode: string): 'error' | 'warning' | 'info' {
    switch (errorCode) {
      case AUTH0_ERROR_CODES.INVALID_GRANT:
        return 'warning'; // Expected when tokens expire
      case AUTH0_ERROR_CODES.LOGIN_REQUIRED:
      case AUTH0_ERROR_CODES.CONSENT_REQUIRED:
      case AUTH0_ERROR_CODES.MISSING_REFRESH_TOKEN:
        return 'info'; // Normal flow
      case AUTH0_ERROR_CODES.POPUP_CLOSED:
        return 'info'; // User action
      default:
        return 'error'; // Unexpected
    }
  },

  /**
   * Execute token cleanup based on error analysis
   */
  cleanup(
    response: AuthErrorResponse,
    accessTokenRef?: React.MutableRefObject<string | undefined>,
  ): void {
    if (response.shouldClearToken) {
      localStorage.removeItem('accessToken');
      if (accessTokenRef) {
        accessTokenRef.current = undefined;
      }
    }
  },
};
